import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { FileText, Download, ArrowLeft } from 'lucide-react';
import UserLayout from './user/UserLayout';

const ProductDocuments = () => {
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const documents = [
    {
      id: 1,
      title: 'STAUFF Product Catalogues',
      description: 'Select STAUFF product group to view, download product catalogues',
      imageUrl: `${process.env.PUBLIC_URL}/resources/catalogue_images/stauff_catalogue.jpg`,
      linkUrl: '/user/product-documents/stauff-catalogues',
    },
    {
      id: 2,
      title: 'Product Approvals',
      description: 'Download certificates and approvals for STAUFF products',
      imageUrl: `${process.env.PUBLIC_URL}/resources/catalogue_images/product_approvals.jpg`,
    },
    {
      id: 3,
      title: 'STAUFF Buyers Guide',
      description: 'Download STAUFF Aus Buyers Guide',
      imageUrl: `${process.env.PUBLIC_URL}/resources/catalogue_images/buyers_guide.jpg`,
    },
  ];

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Product <span className="text-brand">Documents</span>
            </h1>
            <p className="text-gray-600 mt-1">Access product catalogues, approvals, and documentation</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => history.push('/user/dashboard')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </motion.div>

        {/* Document Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {documents.map((doc) => (
            <ProductTile 
              key={doc.id}
              imageUrl={doc.imageUrl}
              title={doc.title}
              description={doc.description}
              linkUrl={doc.linkUrl}
              isMobile={isMobile}
            />
          ))}
        </motion.div>
      </div>
    </UserLayout>
  );
};

const ProductTile = ({ imageUrl, title, description, linkUrl, isMobile }) => {
  const history = useHistory();

  return (
    <motion.div
      whileHover={{ scale: isMobile ? 1 : 1.02 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300"
      onClick={() => linkUrl && history.push(linkUrl)}
    >
      <div className="relative">
        <img src={imageUrl} alt={title} className="w-full h-48 object-cover" />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2">
          <FileText className="w-4 h-4 text-gray-600" />
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex-1">{title}</h3>
          {linkUrl && (
            <Download className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
          )}
        </div>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {linkUrl ? 'Click to browse' : 'Available for download'}
          </span>
          <div className="flex items-center text-blue-600 text-sm font-medium">
            {linkUrl ? 'Browse' : 'Download'}
            <motion.div
              className="ml-1"
              initial={{ x: 0 }}
              whileHover={{ x: 2 }}
              transition={{ duration: 0.2 }}
            >
              →
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductDocuments;
